'use client'

import { useEffect, useRef, useState } from 'react'
import { Bell, CheckCheck, Loader2 } from 'lucide-react'

type Notification = {
  id: number
  type: string
  topicId: number | null
  postId: number | null
  message: string
  actorHandle: string | null
  createdAt: string
  isRead: boolean
}

function formatWhen(value: string) {
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function NotificationBell() {
  const [signedIn, setSignedIn] = useState(false)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)

  async function loadNotifications() {
    const response = await fetch('/api/notifications')
    if (response.status === 401) {
      setSignedIn(false)
      return
    }
    if (!response.ok) return
    const data = (await response.json()) as {
      notifications?: Notification[]
      unreadCount?: number
    }
    setSignedIn(true)
    setNotifications(data.notifications || [])
    setUnreadCount(data.unreadCount || 0)
  }

  useEffect(() => {
    loadNotifications().catch(() => {})
    const interval = window.setInterval(() => loadNotifications().catch(() => {}), 60_000)
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', closeOnOutsideClick)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('mousedown', closeOnOutsideClick)
    }
  }, [])

  async function toggleOpen() {
    const nextOpen = !open
    setOpen(nextOpen)
    if (!nextOpen) return
    setLoading(true)
    try {
      await loadNotifications()
      await fetch('/api/notifications/read', { method: 'POST' })
      setUnreadCount(0)
      setNotifications((current) => current.map((item) => ({ ...item, isRead: true })))
    } finally {
      setLoading(false)
    }
  }

  if (!signedIn) return null

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex min-h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 font-mono text-[10px] font-bold text-primary-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <p className="font-semibold">Notifications</p>
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <CheckCheck className="h-3.5 w-3.5" />
              Marked read
            </span>
          </div>
          <div className="max-h-[min(28rem,65vh)] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : notifications.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                No notifications yet.
              </p>
            ) : (
              notifications.map((notification) => (
                <a
                  key={notification.id}
                  href={
                    notification.topicId
                      ? `/forum/topic/${notification.topicId}${
                          notification.postId ? `#post-${notification.postId}` : ''
                        }`
                      : '/forum'
                  }
                  onClick={() => setOpen(false)}
                  className={`block border-b border-border px-4 py-3 text-sm transition-colors last:border-b-0 hover:bg-secondary/60 ${
                    notification.isRead ? '' : 'bg-primary/5'
                  }`}
                >
                  <span className="block leading-relaxed text-foreground">
                    {notification.message}
                  </span>
                  <time className="mt-1 block text-xs text-muted-foreground">
                    {formatWhen(notification.createdAt)}
                  </time>
                </a>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
